import { NavLink } from "react-router";

import { useSession } from "@/auth/SessionContext";
import { cn } from "@/lib/cn";

import { NAV_GROUPS, type NavItem } from "./navModel";

function NavItemLink({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          isActive
            ? "bg-sidebar-accent text-sidebar-accent-foreground"
            : "text-sidebar-foreground hover:bg-sidebar-accent/60",
        )
      }
    >
      <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
      {item.label}
    </NavLink>
  );
}

/*
 * `UnavailableItem` stood here until #516.
 *
 * It drew a nav entry that was deliberately not a link — no href, out of the
 * tab order, `aria-disabled` with its reason attached — for a section that had
 * no contract behind it yet. `Audit` was its last user, and the canonical
 * product carries no placeholder entry at all, so the renderer went with the
 * `unavailable` variant in `navModel.ts` rather than staying as a construction
 * with nothing to construct.
 */

/**
 * The primary navigation: the canonical reference's appearance, with this
 * application's grouping semantics kept underneath it.
 *
 * ## Why the group headings are hidden rather than deleted (#516)
 *
 * The canonical authority renders these entries in two groups separated by a
 * rule and labels NEITHER group; this application rendered an uppercase
 * `OPERATIONS` and `CLINICAL CONTENT` above them. Manual QA measured the
 * difference against the reference and #516's first Definition-of-Done bullet
 * names sidebar labels explicitly, so the visible headings go.
 *
 * They go from the SCREEN and not from the accessibility tree. Deleting them
 * outright would trade a navigation structure a screen-reader user can move
 * through by heading for an undifferentiated list of links — conformance to the
 * reference buying a real accessibility regression. This repository already
 * holds the position that the reference governs interaction and information
 * architecture rather than accessibility: it is the same reasoning behind the
 * six divergences #516 deliberately preserves, among them accessible names on
 * icon buttons. So `sr-only` rather than a removal, and the separator the
 * reference draws is rendered as the decoration it is — `aria-hidden`, because
 * the grouping it depicts is already carried by the headings.
 */
export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { can } = useSession();

  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    visible: group.items.filter(
      (item) => !item.capability || can(item.capability),
    ),
  })).filter((group) => group.visible.length > 0);

  return (
    <nav aria-label="Primary" className="space-y-2 px-3 py-4">
      {groups.map((group, index) => (
        <div key={group.key} className="space-y-1">
          {/* Between groups only: a leading rule would draw a divider under
              the wordmark that the reference does not have. */}
          {index > 0 ? (
            <hr aria-hidden="true" className="mx-3 my-2 border-t border-sidebar-border" />
          ) : null}
          <h2 className="sr-only">{group.label}</h2>
          {group.visible.map((item) => (
            <NavItemLink
              key={item.key}
              item={item}
              {...(onNavigate ? { onNavigate } : {})}
            />
          ))}
        </div>
      ))}
    </nav>
  );
}
